/**
 * Audit Log Table Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Paginated table for displaying audit log entries.
 */

import React from 'react';
import { ChevronLeft, ChevronRight, Eye, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import type { AuditLogEntry, AuditLogListResponse, AuditSeverity } from '../../../types/admin';
import { SEVERITY_COLORS, SEVERITY_LABELS } from '../../../types/admin';
import { auditService } from '../../../services/admin';
import { Button } from '../../ui/button';

interface AuditLogTableProps {
  data: AuditLogListResponse | null;
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onViewDetails: (entry: AuditLogEntry) => void;
}

const SeverityIcon: React.FC<{ severity: AuditSeverity }> = ({ severity }) => {
  switch (severity) {
    case 'critical':
    case 'error':
      return <AlertCircle size={14} className="text-red-600" />;
    case 'warning':
      return <AlertTriangle size={14} className="text-amber-600" />;
    default:
      return <Info size={14} className="text-blue-600" />;
  }
};

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  data,
  isLoading,
  page,
  onPageChange,
  onViewDetails,
}) => {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Resource
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[...Array(10)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-32" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-5 bg-gray-200 rounded w-16" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-40" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-36" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-28" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-8 bg-gray-200 rounded w-8 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.logs.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <Info size={24} className="text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No audit logs found</h3>
        <p className="text-gray-500">
          No logs match your current filters. Try adjusting your search criteria.
        </p>
      </div>
    );
  }

  const { logs, total, page_size, total_pages } = data;
  const startIndex = (page - 1) * page_size + 1;
  const endIndex = Math.min(page * page_size, total);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Severity
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Event
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Resource
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {logs.map((entry) => (
              <tr
                key={entry.id}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onViewDetails(entry)}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-gray-900">
                    {auditService.formatTimestamp(entry.created_at)}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={`
                      inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                      ${SEVERITY_COLORS[entry.severity]}
                    `}
                  >
                    <SeverityIcon severity={entry.severity} />
                    {SEVERITY_LABELS[entry.severity]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {entry.action}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {auditService.formatEventType(entry.event_type)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {entry.user_email ? (
                    <div>
                      <span className="text-sm text-gray-900">{entry.user_email}</span>
                      {entry.user_role && (
                        <span className="block text-xs text-gray-500 capitalize">
                          {entry.user_role}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">System</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {entry.resource_type ? (
                    <div>
                      <span className="text-sm text-gray-900 capitalize">
                        {entry.resource_type}
                      </span>
                      {entry.resource_name && (
                        <span className="block text-xs text-gray-500 truncate max-w-[200px]">
                          {entry.resource_name}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails(entry);
                    }}
                    className="p-2 text-gray-400 hover:text-[#002B45] hover:bg-gray-100 rounded-lg transition-colors"
                    title="View details"
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <span className="text-sm text-gray-500">
          Showing {startIndex} - {endIndex} of {total} entries
        </span>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft size={16} />
          </Button>

          <div className="flex items-center gap-1">
            {/* Show page numbers */}
            {[...Array(Math.min(5, total_pages))].map((_, i) => {
              let pageNum: number;
              if (total_pages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= total_pages - 2) {
                pageNum = total_pages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`
                    px-3 py-1 text-sm rounded-lg transition-colors
                    ${page === pageNum
                      ? 'bg-[#002B45] text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= total_pages}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogTable;
